import FilterSet from "@/ts/components/Types/FilterSet";
import EtDataType from "@/ts/components/Types/EtDataType";
import PropertyFilter from "@/ts/components/Types/PropertyFilter";
import PropertyValueFilter from "@/ts/components/Types/PropertyValueFilter";
import ApiHandler from "@/ts/components/Handlers/ApiHandler";

declare const etData: EtDataType;

export class FilterSetObject implements FilterSet {
    categoryIds: Array<number>;
    filterSetId: number;
    presenceId: number;
    priceFilterFrom: number;
    priceFilterTo: number;
    propertyFilters: Array<PropertyFilter>;
    propertyValueFilters: Array<PropertyValueFilter>;
    searchTerm: string;
    languageCode: string;
    sortOrder: string;
    additionalUriParameters: Array<Object>;

    constructor() {
        this.categoryIds = [];
        this.presenceId = etData.presenceId;
        this.filterSetId = this.priceFilterFrom = this.priceFilterTo = 0;
        this.propertyFilters = this.propertyValueFilters = [];
        this.searchTerm = this.sortOrder = "";
        this.languageCode = etData.langcode;
    }
}

export default class FilterSetHandler {
    private readonly filterSet: FilterSet | undefined;
    private filterUri: string;
    private readonly original: string;

    constructor() {
        this.addSelectedFilterMouseScrolling();

        if (typeof etData.filterSet == "object") {
            this.original = JSON.stringify(etData.filterSet)
            this.filterSet = JSON.parse(this.original);
        } else {
            this.filterSet = new FilterSetObject();
            this.original = JSON.stringify(this.filterSet);
        }
        this.filterUri = "";

        document.querySelectorAll(".property_toggle").forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                this.togglePropertyFilter(parseInt(button.dataset.list_id), parseInt(button.dataset.property_id));
                const check = button.getElementsByClassName("selection_check");
                if (check[0]) {
                    if (check[0].classList.contains("d-none")) {
                        check[0].classList.remove("d-none");
                    } else {
                        check[0].classList.add("d-none");
                    }
                }
            });
        });
        document.querySelectorAll(".button_reset").forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                let listId = parseInt(button.dataset.list_id);
                this.resetFilter(listId);
                document.querySelectorAll("[data-list_id='" + listId + "']").forEach((element) => {
                    element.querySelectorAll(".selection_check").forEach((check) => {
                        check.classList.add("d-none");
                    });
                });
            });
        });
        document.querySelectorAll(".price_reset").forEach((button) => {
            button.addEventListener("click", () => {
                this.setPriceLimits(0, 0);
            });
        });
        document.querySelectorAll(".button_commence").forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                this.getNewFilterUri(true);
                const listId = parseInt(button.dataset.list_id);
                document.querySelectorAll("[data-id='list_" + listId + "']").forEach((button) => {
                    if (button.ariaExpanded) {
                        button.dispatchEvent(new Event("click"));
                    }
                });
            });
        });
        document.querySelectorAll(".sort_button").forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                this.setSortOrder(button.dataset.sort_order);
            });
        });
        document.querySelectorAll(".price_slider").forEach((input) => {
            input.addEventListener("update", () => {
                const min = document.getElementById("pfrom")?.value;
                const max = document.getElementById("pto")?.value;
                if (typeof min !== "undefined" && typeof max !== "undefined") {
                    this.setPriceLimits(parseInt(min), parseInt(max));
                }
            });
        });

        // d-md-flex ohne d-none führt dazu, dass das collapse element zu beginn ausgeklappt ist
        const topToggleFilter = document.querySelector(".element_narrower__top_toggle_filter");
        const topCollapse = document.querySelector(".element_narrower__top_collapse");
        const toggleCollapseOnce = () => {
            topCollapse?.classList.remove("d-none");
            topToggleFilter?.removeEventListener("click", toggleCollapseOnce);
        };
        topToggleFilter?.addEventListener("click", toggleCollapseOnce, { once: true });
    }

    private addSelectedFilterMouseScrolling() {
        const scrollContainer = document.querySelector('.element_narrower__top');

        if (!scrollContainer) {
            return;
        }

        let isDown = false;
        let startX;
        let scrollLeft;

        const activateScroll = (e) => {
            e.preventDefault()
            if (e.buttons !== 1) return; // Check if the left mouse button is pressed
            isDown = true;
            scrollContainer.classList.add('active');
            startX = e.pageX - scrollContainer.offsetLeft;
            scrollLeft = scrollContainer.scrollLeft;
            if(scrollContainer.scrollWidth === scrollContainer.clientWidth){
                return;
            }
        };

        const deactivateScroll = () => {
            isDown = false;
            scrollContainer.classList.remove('active');
        };

        const scroll = (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - scrollContainer.offsetLeft;
            const walk = (x - startX) * 2;
            scrollContainer.scrollLeft = scrollLeft - walk;
        };

        scrollContainer.addEventListener('mouseup', deactivateScroll);
        scrollContainer.addEventListener('mousemove', scroll);
        scrollContainer.addEventListener('mousedown', (e) => {
            if (e.buttons === 1 && e.target.closest('.element_narrower__top_selected')) {
                activateScroll(e);
            }
        });
    }

    public setSortOrder(sortOrder: string): void {
        if (this.filterSet) {
            this.filterSet.sortOrder = sortOrder;
            this.getNewFilterUri(true);
        }
    }

    public togglePropertyFilter(listId: number, propertyId: number): void {
        if (this.filterSet) {
            for (let i = 0; i < this.filterSet.propertyFilters.length; i++) {
                if (this.filterSet.propertyFilters[i].propertyListId == listId) {
                    const index = this.filterSet.propertyFilters[i].propertyIds.indexOf(propertyId);
                    if (index >= 0) {
                        this.filterSet.propertyFilters[i].propertyIds.splice(index, 1);
                    } else {
                        this.filterSet.propertyFilters[i].propertyIds.push(propertyId);
                    }
                }
            }
        }
    }

    public resetFilter(listId: number): void {
        if (this.filterSet) {
            this.filterSet.propertyFilters.forEach(function (propertyFilter) {
                if (propertyFilter.propertyListId == listId) {
                    propertyFilter.propertyIds = [];
                }
            });
        }
    }

    public setPriceLimits(min: number, max: number) {
        if (this.filterSet) {
            this.filterSet.priceFilterFrom = min;
            this.filterSet.priceFilterTo = max;
        }
    }

    private async getNewFilterUri(redirect?: boolean) {
        if (this.filterSet && this.original != JSON.stringify(this.filterSet)) {
            await ApiHandler.getJsonResponse({
                service: "getFilterLink"
            }, {
                filterSet: JSON.stringify(this.filterSet)
            }).then(
                result => {
                    if (result && result["data"]) {
                        this.filterUri = result["data"];
                        if (typeof redirect !== "undefined" && redirect) {
                            window.location.href = this.filterUri;
                        }
                    }
                }
            )
        }
        Promise.resolve();
    }

    public getFilterUri(): string {
        return this.filterUri;
    }
}